import { defa, T } from 'defa-object';
import template    from 'templates/poppetSnippet.jst.html';
import View        from './view';

/**
 * poppet.Snippet
 * An extended view for listing models, has "open", "settings" and "delete" buttons per Model.
 * Note: This poppet class still has hardcoded defa variables in it. !TODO: Abstractify those variables.
 * @version 0.8.6
 */
const selectors = {
	buttonOpen    : '[data-click=snippet-open]',
	buttonSettings: '[data-click=snippet-settings]',
	buttonDelete  : '[data-click=snippet-delete]',
	buttonBack    : '[data-click=button-back]',
	snippetBusy   : '[data-click=snippet-busy]',
	titleHeader   : '.snippet-title',
	snippetHeader : '.snippet-header',
	titleSecondary: '.snippet-title-secondary',
	
	// from expandSpoiler behavior
	expandToggle : '[data-toggle=expand-spoiler]',
	expandSpoiler: '[data-container=expand-spoiler]',
};

const channelNav = Bb.Radio.channel( 'nav' );

const Snippet = View.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template          : template,
	className         : 'snippet',
	expandedNavState  : false,  // overwrite these nav-states with strings
	compressedNavState: false,  //  to trigger nav-events on expand/implode.
	
	ui: {
		open       : selectors.buttonOpen,
		settings   : selectors.buttonSettings,
		delete     : selectors.buttonDelete,
		back       : selectors.buttonBack,
		headerWrap : selectors.snippetHeader,
		
		header     : selectors.titleHeader,
		secondary  : selectors.titleSecondary,
		
		snippetBusy : selectors.snippetBusy,
		
		// from expandSpoiler behavior
		toggle : selectors.expandToggle,
		spoiler: selectors.expandSpoiler,
	},
	
	events: {
		'click @ui.open'        : 'onClickOpen',
		'click @ui.toggle'      : 'onClickToggle',
		'click @ui.settings'    : 'onClickSettings',
		'click @ui.delete'      : 'onClickDelete',
		'click @ui.back'        : 'onClickBack',
		'click @ui.snippetBusy' : 'onClickBusy',
	},
	
	modelEvents: {
		'change': 'render',
	},
	
	behaviors: {
		ViewAnimator  : {},
		ExpandSpoiler : {},
	},
	
	templateContext: function () {
		return {
			i18n             : T,
			info             : [],
			iconClass        : '',
			header           : this.model.get( 'header' ),
			attributes       : '',
			additionalHeaders: this.getAdditionalHeader(),
			hasRipple        : true,
			canExpand        : false,
			hasBackButton    : false,
			hasContainer     : false,
			hasSettings      : false,
			hasNotifications : false,
			hasMessage       : false,
			hasDelete        : false,
			hasSecondaryTitle: false,
			textRT           : false,
			textRB           : false, // not implemented, !TODO.
		};
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * @constructor
	 */
	initialize: function ( options ) {
		View.prototype.initialize.call( this, options );
		
		if ( this.model ) {
			this.model.snippet = this;
			this.parseInfo();
		}
	},
	
	/**
	 * Triggers when the user clicks the "open" button.
	 * Note: "toggle" and "open" are exclusive.
	 * @void
	 */
	onClickOpen: function ( event ) {
		event.preventDefault();
		
		if ( Mn.getClosestCid( event.target ) !== this.cid ) {
			return;
		}
		
		if ( !this.ui.open.attr( 'disabled' ) ) {
			this.trigger( 'open', this );
		}
	},
	
	/**
	 * Triggers when the user clicks the "toggle" button.
	 * Note: "toggle" and "open" are exclusive.
	 * @void
	 */
	onClickToggle: function ( event ) {
		event.preventDefault();
		
		if ( Mn.getClosestCid( event.target ) !== this.cid ) {
			console.groupEnd();
			return;
		}
		
		let self = this;
		
		// trigger the navigation when the transition ends
		self.$el.one( 'transitionend', function () {
			
			let navEvent = ( self.ui.toggle.hasClass( 'open' ) )
						   ? self.expandedNavState
						   : self.compressedNavState;
			
			if ( navEvent ) {
				channelNav.trigger( navEvent, [ self.model.get( 'id' ), ] );
			}
			
		} );
		
		// starts the transision
		self.$el.toggleClass( 'expanded chosen', ( self.ui.toggle.hasClass( 'open' )));
		//self.ui.headerWrap.toggleClass( 'card', ( self.ui.toggle.hasClass( 'open' )));
		
	},
	
	/**
	 * Triggers when the user clicks the "settings" button.
	 * @void
	 */
	onClickSettings: function ( event ) {
		event.preventDefault();
		
		if ( Mn.getClosestCid( event.target ) !== this.cid ) {
			return;
		}
		
		this.busy();
		this.trigger( 'settings', this );
	},
	
	/**
	 * Triggers when the user clicks the "delete" button.
	 * @void
	 */
	onClickDelete: function ( event ) {
		event.preventDefault();
		this.trigger( 'delete', [this.model.get( 'id' ),]);
	},
	
	/**
	 * Triggeres when the user clicks the "back" button.
	 * @void
	 */
	onClickBack: function ( event ) {
		event.preventDefault();
		
		this.trigger( 'nav', 'back' );
	},
	
	/**
	 * Triggers when the user clicks the snippet bar.
	 * @void
	 */
	onClickBusy: function ( event ) {
		event.preventDefault();
		
		if ( Mn.getClosestCid( event.target ) !== this.cid ) {
			return;
		}
		
		//Override this function to use busy functionality
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Parses the model, looking for an info attribute with a format param.
	 * @void
	 */
	parseInfo: function () {
		let self = this;
		
		_.each( self.model.get( 'info' ), function ( info ) {
			if ( info.format ) {
				info.description = self.formatInfo( info );
			}
		} );
	},
	
	/**
	 * Formats info-parameters by format attribute.
	 * @param  {object} info  An info-object from the response.
	 * @return                A parsed info-object.
	 */
	formatInfo: function ( info ) {
		if ( 'timestamp' === info.format ) {
			let date = new Date( info.description );
			return date.format( defa.vars.dateFormat );
		}
		
		return info;
	},
	/**
	 Implement this method in your specific snippet if you want additional header/info beneath the existing one.
	 @return String Remember to adjust the length o the string due to the height of the snippet
	 */
	getAdditionalHeader: function(){
		return '';
	},
	
} );

export default Snippet;
	
